import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { tasksAPI, kpiAPI, monitoringAPI } from '../services/api';
import { Loading } from '../components/common';
import { formatDate, formatNumber } from '../utils/formatters';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const StatCard = ({ icon: Icon, label, value, color, change, link }) => (
  <Link to={link} className="card hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-800">{formatNumber(value)}</p>
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? <ArrowTrendingUpIcon className="w-4 h-4" /> : <ArrowTrendingDownIcon className="w-4 h-4" />}
          <span>{Math.abs(change)}%</span>
        </div>
      )}
    </div>
  </Link>
);

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [stats, setStats] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [taskStats, kpi, tasks] = await Promise.all([
          tasksAPI.getStatistics(),
          kpiAPI.getDashboard(),
          tasksAPI.getAll({ page_size: 5 }),
        ]);
        setStats(taskStats.data);
        setKpiData(kpi.data);
        setRecentTasks(tasks.data.results || tasks.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Loading />;

  const getStatusBadge = (task) => {
    if (task.is_overdue) return { class: 'badge-danger', text: 'Muddati o\'tgan' };
    switch (task.status) {
      case 'completed': case 'completed_late': return { class: 'badge-success', text: task.status_display };
      case 'new': return { class: 'badge-info', text: 'Yangi' };
      case 'in_progress': case 'pending': return { class: 'badge-warning', text: task.status_display };
      default: return { class: 'badge-gray', text: task.status_display };
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Xush kelibsiz, {user?.first_name || user?.username}!
          </h1>
          <p className="text-gray-500">Bugungi umumiy ko'rsatkichlar</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CalendarIcon className="w-5 h-5" />
          <span>{formatDate(new Date(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard icon={ClipboardDocumentListIcon} label="Jami topshiriqlar" value={stats?.total || 0} color="bg-blue-500" link="/tasks" />
        <StatCard icon={CheckCircleIcon} label="Bajarilgan" value={stats?.completed || 0} color="bg-green-500" link="/tasks?status=completed" />
        <StatCard icon={ClockIcon} label="Jarayonda" value={stats?.pending || 0} color="bg-yellow-500" link="/tasks?status=in_progress" />
        <StatCard icon={ExclamationCircleIcon} label="Muddati o'tgan" value={stats?.overdue || 0} color="bg-red-500" link="/tasks?is_overdue=true" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* KPI Overview */}
        <div className="card lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-blue-600" />
              KPI
            </h3>
            <Link to="/kpi" className="text-sm text-blue-600 hover:underline">Batafsil</Link>
          </div>
          
          {user?.role === 'admin' ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <p className="text-4xl font-bold text-blue-600">{kpiData?.avg_score?.toFixed(1) || 0}</p>
                <p className="text-gray-500 text-sm">O'rtacha ball</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-500">Top 5 OTM:</p>
                {kpiData?.top_5?.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index < 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-700">{item.organization_short_name}</span>
                    </div>
                    <span className="font-semibold text-blue-600">{item.total_score}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-5xl font-bold text-blue-600">{kpiData?.my_score || 0}</p>
              <p className="text-gray-500 mt-2">Sizning KPI ballingiz</p>
              {kpiData?.my_rank && (
                <p className="text-sm text-gray-400 mt-1">
                  Reyting: <span className="font-semibold">#{kpiData.my_rank}</span>
                </p>
              )}
              <div className="mt-4 pt-4 border-t">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all"
                    style={{ width: `${Math.min((kpiData?.my_score || 0), 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">100 balldan</p>
              </div>
            </div>
          )}
        </div>

        {/* Recent Tasks */}
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">So'nggi topshiriqlar</h3>
            <Link to="/tasks" className="text-sm text-blue-600 hover:underline">Barchasini ko'rish</Link>
          </div>
          
          {recentTasks.length > 0 ? (
            <div className="space-y-3">
              {recentTasks.map((task) => {
                const badge = getStatusBadge(task);
                return (
                  <Link
                    key={task.id}
                    to={`/tasks/${task.id}`}
                    className="block p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{task.title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-500">{task.category_name}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className={`text-sm ${task.is_overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            {formatDate(task.deadline)}
                          </span>
                        </div>
                      </div>
                      <span className={`badge ${badge.class}`}>{badge.text}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <ClipboardDocumentListIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Topshiriqlar yo'q</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions for Admin */}
      {user?.role === 'admin' && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Tezkor harakatlar</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Link to="/tasks" className="p-4 bg-blue-50 rounded-xl text-center hover:bg-blue-100 transition-colors">
              <ClipboardDocumentListIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-blue-800">Yangi topshiriq</p>
            </Link>
            <Link to="/organizations" className="p-4 bg-green-50 rounded-xl text-center hover:bg-green-100 transition-colors">
              <CheckCircleIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-800">Tashkilotlar</p>
            </Link>
            <Link to="/kpi" className="p-4 bg-purple-50 rounded-xl text-center hover:bg-purple-100 transition-colors">
              <ChartBarIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-purple-800">KPI hisoblash</p>
            </Link>
            <Link to="/reports" className="p-4 bg-yellow-50 rounded-xl text-center hover:bg-yellow-100 transition-colors">
              <CalendarIcon className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-yellow-800">Hisobotlar</p>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
